/**
 * @description
 * Client configuration tab component for managing environment variables
 * and configuration settings for VibeSDK deployment.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { Link } from 'react-router';
import type { ClientConfigurationProps } from './types';

const CONFIG_CATEGORIES = {
  ai_providers: {
    title: 'AI Providers',
    description: 'Configure API keys for your preferred AI model providers. Google AI Studio (Gemini) is recommended for best compatibility.',
    configs: [
      { key: 'GOOGLE_AI_STUDIO_API_KEY', description: 'Google Gemini models (Recommended - default config)', isRequired: false, isSecret: true },
      { key: 'OPENAI_API_KEY', description: 'OpenAI GPT models', isRequired: false, isSecret: true },
      { key: 'ANTHROPIC_API_KEY', description: 'Anthropic Claude models', isRequired: false, isSecret: true },
      { key: 'CEREBRAS_API_KEY', description: 'Cerebras open source models', isRequired: false, isSecret: true },
      { key: 'OPENROUTER_API_KEY', description: 'OpenRouter (various models)', isRequired: false, isSecret: true },
      { key: 'GROQ_API_KEY', description: 'Groq models', isRequired: false, isSecret: true },
    ]
  },
  security: {
    title: 'Security & Secrets',
    description: 'Client-specific authentication and webhook secrets',
    configs: [
      { key: 'JWT_SECRET', description: 'JWT signing secret (auto-generated)', isRequired: true, isSecret: true },
      { key: 'WEBHOOK_SECRET', description: 'Webhook verification secret (auto-generated)', isRequired: true, isSecret: true },
    ]
  },
  cloudflare_gateway: {
    title: 'Cloudflare AI Gateway',
    description: 'Optional client-specific AI Gateway configuration',
    configs: [
      { key: 'CLOUDFLARE_AI_GATEWAY_TOKEN', description: 'AI Gateway token (optional override)', isRequired: false, isSecret: true },
      { key: 'CLOUDFLARE_AI_GATEWAY_URL', description: 'AI Gateway URL (optional override)', isRequired: false, isSecret: false },
    ]
  },
  environment: {
    title: 'Environment Settings',
    description: 'Client-specific deployment configuration',
    configs: [
      { key: 'CUSTOM_DOMAIN', description: 'Custom domain (defaults to subdomain.byteable.ai)', isRequired: false, isSecret: false },
      { key: 'ENVIRONMENT', description: 'Environment (prod/staging/dev)', isRequired: false, isSecret: false },
    ]
  }
};

export function ClientConfiguration({
  clientId: _clientId,
  configs,
  configsLoading,
  editingKey,
  editValue,
  showSecrets,
  saving,
  onInitializeDefaults,
  onSaveConfig,
  onDeleteConfig,
  onStartEdit,
  onCancelEdit,
  onToggleSecretVisibility,
}: ClientConfigurationProps) {
  const maskValue = (value: string) => {
    if (!value) return '';
    return '•'.repeat(Math.min(value.length, 32));
  };

  if (configsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">VibeSDK Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage environment variables for this client's VibeSDK deployment
          </p>
        </div>
        <button
          onClick={onInitializeDefaults}
          disabled={saving || configsLoading}
          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
        >
          {saving ? 'Initializing...' : 'Initialize Defaults'}
        </button>
      </div>

      {/* Global Settings Notice */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <svg className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Global Settings Inherited</h4>
            <p className="text-sm text-blue-800">
              OAuth credentials (Google, GitHub) and Cloudflare account settings are configured globally in the{' '}
              <Link to="/settings" className="font-medium underline hover:text-blue-900">
                Settings
              </Link>{' '}
              page and automatically applied to all client deployments. Only client-specific configurations are shown below.
            </p>
          </div>
        </div>
      </div>

      {/* AI Provider Configuration Notice */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-amber-900 mb-1">AI Provider Configuration</h4>
            <p className="text-sm text-amber-800 mb-2">
              <strong>Google AI Studio (Gemini) is recommended</strong> as it works with default model configurations.
              Other providers require editing <code className="bg-amber-100 px-1 rounded">worker/agents/inferutils/config.ts</code> to change model configurations.
            </p>
            <p className="text-sm text-amber-800">
              You can configure multiple AI providers. At least one provider is needed for the platform to function.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Categories */}
      {Object.entries(CONFIG_CATEGORIES).map(([categoryKey, category]) => (
        <div key={categoryKey} className="rounded-lg border bg-card">
          <div className="border-b bg-muted/50 px-6 py-4">
            <h3 className="text-lg font-semibold">{category.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
          </div>
          <div className="p-6 space-y-4">
            {category.configs.map((configDef) => {
              const currentValue = configs[configDef.key] || '';
              const isEditing = editingKey === configDef.key;
              const isSecret = configDef.isSecret;
              const showValue = showSecrets[configDef.key];

              return (
                <div key={configDef.key} className="flex items-start gap-4 pb-4 border-b last:border-0 last:pb-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-sm font-mono font-medium">{configDef.key}</code>
                      {configDef.isRequired && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Required</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{configDef.description}</p>
                    
                    {isEditing ? (
                      <div className="flex gap-2">
                        <input
                          type={isSecret && !showValue ? 'password' : 'text'}
                          value={editValue}
                          onChange={(_e) => {/* This would be handled by parent */}}
                          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          placeholder={`Enter ${configDef.key}`}
                          autoFocus
                        />
                        <button
                          onClick={() => onSaveConfig(configDef.key)}
                          disabled={saving}
                          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={onCancelEdit}
                          disabled={saving}
                          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-mono">
                          {currentValue ? (
                            isSecret && !showValue ? maskValue(currentValue) : currentValue
                          ) : (
                            <span className="text-muted-foreground italic">Not set</span>
                          )}
                        </code>
                        {isSecret && currentValue && (
                          <button
                            onClick={() => onToggleSecretVisibility(configDef.key)}
                            className="rounded-md border border-input bg-background p-2 hover:bg-accent"
                            title={showValue ? 'Hide value' : 'Show value'}
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              {showValue ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              )}
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => onStartEdit(configDef.key, currentValue)}
                          disabled={saving}
                          className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
                        >
                          Edit
                        </button>
                        {currentValue && !configDef.isRequired && (
                          <button
                            onClick={() => onDeleteConfig(configDef.key)}
                            disabled={saving}
                            className="rounded-md border border-red-200 bg-background px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
